import { useFlags } from '@atlaskit/flag';
import { IDelivery } from 'core/api/deliveries/deliveries-api-interface';
import DeliveriesApiService from 'core/api/deliveries/deliveries-api.service';
import { ChangeDeliveryStatusFormFields } from 'core/config/form-fields';
import { DeliveryStatus } from 'core/constants/delivery-status';
import { useAuthState } from 'core/providers/AuthProvider';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import dayjs from 'dayjs';
import { useState } from 'react';
import { IButton } from 'shared/components/buttons/button-interface';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import SharedForm from 'shared/components/form/form';
import { IChangeDeliveryStatusDialogFormOutput } from './change-delivery-status-dialog-interface';

interface IChangeDeliveryStatusDialog {
  delivery: IDelivery;
}

const generateSignaturePng = (text: string): string => {
  const canvas = document.createElement('canvas');
  canvas.width = 600;
  canvas.height = 200;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to acquire canvas context');
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#111827';
  ctx.font = 'italic 28px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  return canvas.toDataURL('image/png');
};

const ChangeDeliveryStatusDialog = ({ delivery }: IChangeDeliveryStatusDialog) => {
  const [formSubmitting, setFormSubmitting] = useState(false);
  const dialog = useDialog();
  const flags = useFlags();
  const { userData } = useAuthState();

  const submit = async (data: IChangeDeliveryStatusDialogFormOutput) => {
    const newStatus = data.status.value;
    if (newStatus === DeliveryStatus.DELIVERED && !data.deliveredAt) {
      showErrorFlag('Date required', 'Please pick the date this delivery took place.', flags);
      return;
    }
    if (newStatus === DeliveryStatus.UNFULFILLED && !data.driverNote?.trim()) {
      showErrorFlag('Comment required', 'Please provide a reason for marking as unfulfilled.', flags);
      return;
    }

    setFormSubmitting(true);
    try {
      if (newStatus === DeliveryStatus.DELIVERED) {
        const actor = {
          uid: userData?.uid ?? '',
          fullName: userData?.fullName ?? 'Admin',
        };
        const signatureText = `Marked as delivered by ${actor.fullName}`;
        const dataUrl = generateSignaturePng(signatureText);
        await DeliveriesApiService.uploadDeliverySignature(delivery.uid, dataUrl);
        await DeliveriesApiService.update(delivery.uid, {
          status: DeliveryStatus.DELIVERED,
          proofMethod: 'signature',
          deliveredAt: dayjs(data.deliveredAt).toDate(),
          deliveredBy: actor,
          ...(data.driverNote?.trim() && { driverNote: data.driverNote.trim() }),
        });
      } else {
        await DeliveriesApiService.update(delivery.uid, {
          status: DeliveryStatus.UNFULFILLED,
          driverNote: data.driverNote!.trim(),
        });
      }
      dialog?.closeDialog();
      showSuccessFlag('Status updated', `Delivery status was set to ${newStatus}.`, flags);
    } catch (error) {
      console.log(error);

      showErrorFlag('Update failed', 'Unable to change the delivery status, please try again.', flags);
      setFormSubmitting(false);
    }
  };

  const cancelButton: IButton = {
    onClick: () => dialog?.closeDialog(),
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  const customContent = () => (
    <SharedForm
      className='p-4 overflow-y-auto'
      onSubmit={submit}
      fields={ChangeDeliveryStatusFormFields}
      buttonLabel='Save'
      loading={formSubmitting}
      cancelButton={cancelButton}
    />
  );

  return (
    <SharedDialogBase title='Change delivery status' customContentTemplate={customContent()} showButtons={false} />
  );
};

export default ChangeDeliveryStatusDialog;
